import type { DataSource, DayEntry, DayMark, DaySample, ISODate, Member, MemberId, PostDoc, ProfileDoc, UiError } from '../contract.js';
import { monthFromKey, monthKey, monthOf } from '../dates.js';
import { initialState } from './types.js';
import type { AppState, Patch } from './types.js';

export interface StoreEnv {
  readonly data: DataSource;
  /** The reduced-motion switch, read when each action runs (03 §8). */
  readonly reduced?: () => boolean;
  /** Where unexpected errors go (never shown to the user, never containing post text or media URLs). */
  readonly log?: (error: UiError) => void;
  /** The time in ms, for how old loaded data is (tests replace it). */
  readonly now?: () => number;
}

/**
 * A day or profile opened again this long after it was read is read again in the background: the
 * backend's media addresses last five minutes (07 §3), so older ones may no longer load.
 */
export const STALE_MS = 240_000;

type Timer = ReturnType<typeof setTimeout>;

/** Turn whatever a DataSource rejected with into a UiError. */
export function uiError(value: unknown): UiError {
  if (value && typeof value === 'object' && 'kind' in value && typeof (value as { kind: unknown }).kind === 'string') return value as UiError;
  return { kind: 'server', detail: value instanceof Error ? value.name : 'unknown' };
}

/**
 * The app's state container: one state object, one cancellable set of choreography timers
 * (`later` / `clearLater`, 03 §0) and the session's data cache (06 §5.5).
 * Actions live in the neighbouring modules and work through this class.
 */
export class AppStore {
  readonly data: DataSource;
  readonly reduced: () => boolean;
  private readonly log: (error: UiError) => void;
  private readonly now: () => number;
  private state: AppState;
  private readonly listeners = new Set<() => void>();
  private readonly timers = new Set<Timer>();
  private toastTimer: Timer | null = null;
  private readonly inflight = new Map<string, Promise<void>>();
  /** Bumped by `invalidate`, so answers to older requests are dropped. */
  private readonly epoch = new Map<string, number>();
  /** Hooks other modules attach: stop playback, forget the pointer gesture, react to a day's data arriving. */
  stopPlayback: () => void = () => undefined;
  resetPointer: () => void = () => undefined;
  onDayLoaded: (date: ISODate) => void = () => undefined;
  /** Uploads still running (04 §11.7): saving waits for them. */
  private readonly uploads = new Set<Promise<unknown>>();
  /** How many uploads have failed so far; a save that was waiting for uploads stops when this moves. */
  uploadFailures = 0;

  constructor(env: StoreEnv) {
    this.data = env.data;
    this.state = initialState(env.data.capabilities);
    this.reduced = env.reduced ?? (() => false);
    this.log = env.log ?? (() => undefined);
    this.now = env.now ?? (() => Date.now());
  }

  // ------------------------------------------------------------ state
  getState = (): AppState => this.state;
  subscribe = (listener: () => void): (() => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  set(patch: Patch): void {
    this.state = { ...this.state, ...patch };
    for (const listener of [...this.listeners]) listener();
  }

  // ------------------------------------------------------------ choreography timers
  /** Schedule one step of a choreography. */
  later(ms: number, step: () => void): void {
    const id: Timer = setTimeout(() => { this.timers.delete(id); step(); }, ms);
    this.timers.add(id);
  }
  /** Cancel every pending step. Every navigation starts with this, so the newest one always wins. */
  clearLater(): void {
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
  }

  // ------------------------------------------------------------ toast (03 §7)
  flash(text: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.set({ toast: text });
    this.toastTimer = setTimeout(() => { this.toastTimer = null; this.set({ toast: null }); }, 2600);
  }

  /** Nobody is signed in any more: forget every piece of the previous member's data. */
  signedOut(): void {
    this.clearLater();
    this.stopPlayback();
    this.resetPointer();
    this.inflight.clear();
    for (const key of [...this.epoch.keys()]) this.epoch.set(key, (this.epoch.get(key) ?? 0) + 1);
    this.state = { ...initialState(this.data.capabilities), boot: 'signed-out' };
    for (const listener of [...this.listeners]) listener();
  }

  dispose(): void {
    this.clearLater();
    this.stopPlayback();
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = null;
  }

  // ------------------------------------------------------------ uploads
  /** Remember an upload until it settles. */
  trackUpload<T>(upload: Promise<T>): Promise<T> {
    const settled = upload.then(() => undefined, () => undefined).finally(() => { this.uploads.delete(settled); });
    this.uploads.add(settled);
    return upload;
  }
  /** Resolves when every upload that is running now has finished, whatever its outcome. */
  async uploadsSettled(): Promise<void> {
    while (this.uploads.size) await Promise.all([...this.uploads]);
  }

  // ------------------------------------------------------------ data
  /** Any call can discover that nobody is signed in; the app then hands over to sign-in (06 §5.7). */
  report(error: UiError): void {
    if (error.kind === 'auth') { if (this.state.boot !== 'signed-out') this.set({ boot: 'signed-out' }); }
    else if (error.kind === 'profile_required') { if (this.state.boot !== 'needs-profile') this.set({ boot: 'needs-profile' }); }
    else this.log(error);
  }
  private once(key: string, run: () => Promise<void>): Promise<void> {
    const pending = this.inflight.get(key);
    if (pending) return pending;
    const promise = run().finally(() => { if (this.inflight.get(key) === promise) this.inflight.delete(key); });
    this.inflight.set(key, promise);
    return promise;
  }
  /** Drop an in-flight request for `key`: its answer will be ignored. */
  private invalidate(key: string): void {
    this.epoch.set(key, (this.epoch.get(key) ?? 0) + 1);
    this.inflight.delete(key);
  }
  private stamp(key: string): () => boolean {
    const at = this.epoch.get(key) ?? 0;
    return () => (this.epoch.get(key) ?? 0) === at;
  }

  /** Load the session, then the month that holds today. Called at start and by "Try again". */
  async start(): Promise<void> {
    const first = this.state.session === null;
    if (first) this.set({ boot: 'loading' });
    try {
      const session = await this.data.getSession();
      const key = monthKey(monthOf(session.today));
      const boot = session.firstRun && this.data.setColour ? 'choose-colour' : 'ready';
      // Only the first answer places the screen on today: a start that overlapped it (React starts the app twice
      // in development) must not move a page that an address already opened (owner, 4 October 2026).
      const placing = this.state.session === null;
      this.set({ session, boot, ...(placing ? { month: key, date: session.today, kDate: session.today, cDate: session.today } : {}) });
      void this.loadMonth(key);
      if (placing) void this.loadCatalogues();
    } catch (reason) {
      const error = uiError(reason);
      if (error.kind === 'auth') this.set({ boot: 'signed-out' });
      else if (error.kind === 'profile_required') this.set({ boot: 'needs-profile' });
      else { this.log(error); if (first) this.set({ boot: 'error' }); }
    }
  }
  /** Re-read the session without disturbing the screen (after saving your profile; when today may have changed). */
  refreshSession(): Promise<void> { return this.start(); }

  private async loadCatalogues(): Promise<void> {
    const { capabilities } = this.data;
    try { if (capabilities.presetPictures && this.data.listPresetPictures) this.set({ presets: await this.data.listPresetPictures() }); }
    catch (reason) { this.report(uiError(reason)); }
    try { if (capabilities.songs && this.data.listSongs) this.set({ songs: await this.data.listSongs() }); }
    catch (reason) { this.report(uiError(reason)); }
  }

  /** Who posted on each day of a month. Cached for the session; `force` refreshes it and keeps showing the old data meanwhile. */
  loadMonth(key: string, force = false): Promise<void> {
    const current = this.state.months[key];
    if (!force && current?.status === 'loaded') return Promise.resolve();
    if (force) this.invalidate(`m:${key}`);
    return this.once(`m:${key}`, async () => {
      const fresh = this.stamp(`m:${key}`);
      if (!this.state.months[key]?.days) this.set({ months: { ...this.state.months, [key]: { status: 'loading', days: null } } });
      try {
        const summary = await this.data.getMonth(monthFromKey(key));
        if (fresh()) this.set({ months: { ...this.state.months, [key]: { status: 'loaded', days: summary.days } } });
      } catch (reason) {
        this.report(uiError(reason));
        if (fresh() && !this.state.months[key]?.days) this.set({ months: { ...this.state.months, [key]: { status: 'error', days: null } } });
      }
    });
  }

  /** Loaded data that has outlived its media addresses. */
  private stale(at: number | undefined): boolean { return at !== undefined && this.now() - at > STALE_MS; }

  /** Every post of one day. Cached for the session (read again once stale); `force` refreshes it. */
  loadDay(date: ISODate, force = false): Promise<void> {
    const current = this.state.days[date];
    if (!force && current?.status === 'loaded' && !this.stale(current.at)) return Promise.resolve();
    // Only `force` starts over. A stale day is read again too, but joins a read that is already on its way:
    // going through its posts asks for it at every step, and one answer serves them all.
    if (force) this.invalidate(`d:${date}`);
    return this.once(`d:${date}`, async () => {
      const fresh = this.stamp(`d:${date}`);
      if (!this.state.days[date]?.entries) this.set({ days: { ...this.state.days, [date]: { status: 'loading', entries: null } } });
      try {
        const day = await this.data.getDay(date);
        if (!fresh()) return;
        this.set({ days: { ...this.state.days, [date]: { status: 'loaded', entries: this.inMemberOrder(day.entries), at: this.now() } }, people: this.met(day.entries.map((entry) => entry.author)) });
        this.onDayLoaded(date);
      } catch (reason) {
        this.report(uiError(reason));
        if (fresh() && !this.state.days[date]?.entries) this.set({ days: { ...this.state.days, [date]: { status: 'error', entries: null } } });
      }
    });
  }

  /** A member's profile. Cached for the session (read again once stale); `force` refreshes it. */
  loadProfile(id: MemberId, force = false): Promise<void> {
    const current = this.state.profiles[id];
    if (!force && current?.status === 'loaded' && !this.stale(current.at)) return Promise.resolve();
    if (current?.status === 'loaded') force = true;
    if (force) this.invalidate(`p:${id}`);
    return this.once(`p:${id}`, async () => {
      const fresh = this.stamp(`p:${id}`);
      if (!this.state.profiles[id]?.doc) this.set({ profiles: { ...this.state.profiles, [id]: { status: 'loading', doc: null } } });
      try {
        const doc = await this.data.getProfile(id);
        if (!fresh()) return;
        const known = memberLike(this.state, id);
        this.set({ profiles: { ...this.state.profiles, [id]: { status: 'loaded', doc, at: this.now() } }, ...(known ? { people: this.met([{ ...known, name: doc.name, color: doc.color, pic: doc.pic }]) } : {}) });
      } catch (reason) {
        this.report(uiError(reason));
        if (fresh() && !this.state.profiles[id]?.doc) this.set({ profiles: { ...this.state.profiles, [id]: { status: 'error', doc: null } } });
      }
    });
  }

  /** Somebody found outside the day and profile reads (by handle, for a page address) joins the people cache. */
  meet(member: Member): void {
    if (member.id !== this.state.session?.me.id) this.set({ people: this.met([member]) });
  }

  /** The people cache with these people as now seen (you stay in the session). */
  private met(people: readonly Member[]): AppState['people'] {
    const mine = this.state.session?.me.id;
    const next = { ...this.state.people };
    for (const person of people) if (person.id !== mine) next[person.id] = { ...person, isYou: false };
    return next;
  }

  /**
   * Entries follow member display order where the session lists the members (06 §5.3); people it
   * does not list keep the order the source gave, after them.
   */
  private inMemberOrder(entries: readonly DayEntry[]): DayEntry[] {
    const order = new Map((this.state.session?.members ?? []).map((member, index) => [member.id, index]));
    return entries.filter((entry) => entry.post.items.length > 0)
      .map((entry, index) => ({ entry, index }))
      .sort((a, b) => (order.get(a.entry.memberId) ?? 1e9) - (order.get(b.entry.memberId) ?? 1e9) || a.index - b.index)
      .map(({ entry }) => entry);
  }

  /**
   * Put your just-saved post (or its removal) into the cache, so the next screen is right even if the
   * refetch fails. Your entry and stripe colour keep their place, or take yours in member order; where
   * that order is not known they go last until the refetch.
   */
  patchOwnPost(date: ISODate, post: PostDoc | null): void {
    const session = this.state.session;
    if (!session) return;
    const mine = session.me.id, key = monthKey(monthOf(date));
    const has = !!post && post.items.length > 0;
    const day = this.state.days[date], month = this.state.months[key];
    let days = this.state.days, months = this.state.months;
    if (day?.status === 'loaded' && day.entries) {
      const entries = day.entries.filter((entry) => entry.memberId !== mine);
      if (has && post) entries.splice(placeOf(day.entries, entries.length, (entry) => entry.memberId === mine), 0, { memberId: mine, author: session.me, post });
      days = { ...days, [date]: { ...day, status: 'loaded', entries: this.inMemberOrder(entries) } };
    }
    if (month?.days) {
      const before: DayMark | undefined = month.days[date];
      const samples = (before?.samples ?? []).filter((sample) => sample.id !== mine);
      if (has) samples.splice(placeOf(before?.samples ?? [], samples.length, (sample) => sample.id === mine), 0, { id: mine, color: session.me.color });
      const order = new Map(session.members.map((member, index) => [member.id, index]));
      const sorted = samples.map((sample, index) => ({ sample, index })).sort((a, b) => (order.get(a.sample.id) ?? 1e9) - (order.get(b.sample.id) ?? 1e9) || a.index - b.index).map(({ sample }): DaySample => sample);
      const had = before?.mine ?? false, count = Math.max(0, (before?.count ?? 0) + (has && !had ? 1 : !has && had ? -1 : 0));
      const next = { ...month.days };
      if (count > 0) next[date] = { count, samples: sorted, mine: has }; else delete next[date];
      months = { ...months, [key]: { status: month.status, days: next } };
    }
    this.set({ days, months });
  }

  /** Put your just-saved profile into the cache. */
  patchOwnProfile(doc: ProfileDoc): void {
    const mine = this.state.session?.me.id;
    if (mine) this.set({ profiles: { ...this.state.profiles, [mine]: { status: 'loaded', doc, at: this.now() } } });
  }
}

/** Where an item that was somewhere in `list` goes back into the list without it: its old place, else the end. */
function placeOf<T>(list: readonly T[], length: number, was: (item: T) => boolean): number {
  const at = list.findIndex(was);
  return at < 0 ? length : Math.min(at, length);
}

/** Someone already known (a session member or met in the data), to refresh from their profile. */
function memberLike(st: AppState, id: MemberId): Member | null {
  if (!st.session || id === st.session.me.id) return null;
  return st.people[id] ?? st.session.members.find((member) => member.id === id) ?? null;
}
